// Checks achievement conditions at key game moments and calls unlock on the store.

import { AchievementStore } from "./achievements";
import { showAchievementNotification } from "../ui/AchievementNotification";
import type { Unit } from "../units/Unit";
import type { RosterEntry } from "./RunState";
import { CHAMPIONS } from "../data/champions";
import { ACTS } from "../data/acts";
import type { Difficulty } from "../data/difficulty";
import type { EventBus } from "../engine/event/EventBus";
import type { CombatEvents } from "../combat/events";

export class AchievementTracker {
  private hadPlayerDeath = false;
  private runDamageByChampion: Record<string, number> = {};

  private combatPlayerDeaths = 0;
  private combatEnemyDeaths = 0;
  private combatRounds = 0;
  private combatHealingAccum = 0;
  private combatCastCount = 0;
  private combatCcCount = 0;

  constructor(readonly store: AchievementStore) {}

  onRunStart(): void {
    this.hadPlayerDeath = false;
    this.runDamageByChampion = {};
  }

  onCombatStart(bus: EventBus<CombatEvents>): void {
    this.combatPlayerDeaths = 0;
    this.combatEnemyDeaths = 0;
    this.combatRounds = 0;
    this.combatHealingAccum = 0;
    this.combatCastCount = 0;
    this.combatCcCount = 0;

    bus.on("unit.casted", () => { this.combatCastCount++; });
    bus.on("unit.statusApplied", (e) => {
      if (e.effect.kind !== "shield") this.combatCcCount++;
    });
    bus.on("unit.died", (e) => {
      if (e.unit.team === "enemy") this.combatEnemyDeaths++;
      else this.combatPlayerDeaths++;
    });
    bus.on("combat.turnEnded", () => { this.combatRounds++; });
    bus.on("unit.healed", (e) => {
      if (e.unit.team === "player") this.combatHealingAccum += e.amount;
    });
  }

  onCombatEnd(
    winner: "player" | "enemy",
    players: Unit[],
    _nodeType: string,
    _roster?: RosterEntry[],
  ): void {
    if (winner !== "player") return;

    if (this.combatPlayerDeaths > 0) this.hadPlayerDeath = true;

    this.store.stats.totalCombatsWon++;
    this.store.saveStats();

    for (const u of players) {
      const champId = u.championDef?.id;
      if (!champId) continue;

      if (u.damageDealt > 0) {
        const newTotal = this.store.addChampionDamage(champId, u.damageDealt);
        this.store.setProgress(`damage_${champId}`, newTotal);
        if (newTotal >= 1_000_000) {
          this.tryUnlock(`damage_${champId}`);
        }
        this.runDamageByChampion[champId] =
          (this.runDamageByChampion[champId] ?? 0) + u.damageDealt;
      }
    }

    // Flush per-combat stats to persistent totals
    this.store.stats.totalEnemiesKilled += this.combatEnemyDeaths;
    this.store.stats.totalHealingDone += this.combatHealingAccum;
    this.store.stats.totalAbilityCasts += this.combatCastCount;
    this.store.saveStats();

    // Sync progress bar achievements (absolute values, retroactive-safe)
    this.syncProgress("first_victory",         this.store.stats.totalCombatsWon);
    this.syncProgress("ten_victories",         this.store.stats.totalCombatsWon);
    this.syncProgress("twenty_five_victories", this.store.stats.totalCombatsWon);
    this.syncProgress("fifty_victories",       this.store.stats.totalCombatsWon);
    this.syncProgress("hundred_victories",     this.store.stats.totalCombatsWon);
    this.syncProgress("two_fifty_victories",   this.store.stats.totalCombatsWon);
    this.syncProgress("hundred_enemies",       this.store.stats.totalEnemiesKilled);
    this.syncProgress("five_hundred_enemies",  this.store.stats.totalEnemiesKilled);
    this.syncProgress("two_thousand_enemies",  this.store.stats.totalEnemiesKilled);
    this.syncProgress("ten_thousand_enemies",  this.store.stats.totalEnemiesKilled);
    this.syncProgress("total_healing",         this.store.stats.totalHealingDone);
    this.syncProgress("healing_million",       this.store.stats.totalHealingDone);
    this.syncProgress("thousand_casts",        this.store.stats.totalAbilityCasts);
    this.syncProgress("five_k_casts",          this.store.stats.totalAbilityCasts);

    // Per-combat achievements
    if (this.combatCastCount >= 15)    this.tryUnlock("ability_barrage");
    if (this.combatCcCount >= 5)       this.tryUnlock("cc_master");
  }

  onActComplete(actNumber: number, roster: RosterEntry[], difficulty: Difficulty = "normal"): void {
    if (actNumber === 1) this.tryUnlock("act_one_clear");
    if (actNumber >= ACTS.length) {
      this.store.stats.completedRuns++;
      // Any completion unlocks Hard. Hard or Impossible completion unlocks Impossible.
      this.store.stats.hardUnlocked = true;
      if (difficulty === "hard" || difficulty === "impossible") {
        this.store.stats.impossibleUnlocked = true;
      }
      this.store.saveStats();
      this.syncProgress("three_runs", this.store.stats.completedRuns);
      this.syncProgress("ten_runs",   this.store.stats.completedRuns);
      this.checkRunWinAchievements(roster, difficulty);
    }
  }

  onRecruit(roster: RosterEntry[]): void {
    if (roster.length >= 3)                          this.tryUnlock("full_roster");
    if (roster.some((e) => (e.stars ?? 1) >= 3))    this.tryUnlock("three_star");
    if (roster.some((e) => (e.stars ?? 1) >= 2))    this.tryUnlock("two_star");
  }

  onRosterUpdate(_roster: RosterEntry[]): void {}

  private syncProgress(id: string, total: number): void {
    const unlocked = this.store.setProgress(id, total);
    if (unlocked) showAchievementNotification(unlocked);
  }

  private checkRunWinAchievements(roster: RosterEntry[], difficulty: Difficulty = "normal"): void {
    const champDefs = roster.map((e) => CHAMPIONS[e.championId]).filter(Boolean);

    if (champDefs.length > 0 && champDefs.every((c) => c.region === "demacia")) {
      this.tryUnlock("demacian_glory");
    }

    if (champDefs.length > 0 && champDefs.every((c) => c.region === "piltover")) {
      this.tryUnlock("piltover_zaun_pride");
    }

    if (roster.some((e) => e.championId === "KaiSa")) {
      this.tryUnlock("kaisa_carry");
    }

    if (roster.every((e) => (e.stars ?? 1) === 1)) {
      this.tryUnlock("one_star_only");
    }

    if (champDefs.every((c) => c.tier === "common")) {
      this.tryUnlock("common_ground");
    }

    if (!this.hadPlayerDeath) {
      this.tryUnlock("deathless");
    }

    if (roster.every((e) => !e.redBuff && !e.blueBuff)) {
      this.tryUnlock("pure_skill");
    }

    const dealerCount = Object.values(this.runDamageByChampion).filter((d) => d > 0).length;
    if (dealerCount === 1) {
      this.tryUnlock("sole_dealer");
    }

    if (champDefs.every((c) => c.range === 1)) {
      this.tryUnlock("range_free");
    }

    if (champDefs.every((c) => c.range > 1)) {
      this.tryUnlock("melee_free");
    }

    if (difficulty === "hard" || difficulty === "impossible") {
      this.tryUnlock("beat_hard");
    }

    if (difficulty === "impossible") {
      this.tryUnlock("beat_impossible");
      if (champDefs.every((c) => c.tier !== "legendary")) {
        this.tryUnlock("impossible_no_legendary");
      }
    }
  }

  private tryUnlock(id: string): void {
    const def = this.store.unlock(id);
    if (def) showAchievementNotification(def);
  }
}
